/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import { HnaRequest, HnaZone } from '../../../shared/entities/hnaRequest';
import { PlaylistAddCheckRounded } from '@mui/icons-material';
import Button from '@mui/material/Button';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { isNil } from "lodash";
import BeamMenu from "../../common/beam/BeamMenu";
import BeamMenuItem from "../../common/beam/BeamMenuItem";
import BeamTooltip from "../../common/beam/BeamTooltip";

interface GenerateHnaRequestButtonProps {
  hnaZones?: HnaZone[];
  antennaType?: string;
  tailNumber?: string;
  missionPlanId?: number;
}

const GenerateHnaRequestButton = (props: GenerateHnaRequestButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };
  const handleClose = () => {
    setIsOpen(false);
  };

  const isMissingHnaZones = !props.hnaZones || props.hnaZones.length === 0;
  const newHnaRequestRoute = '/plans/hna-requests/new';

  let prefilledMenuItem = (
    <RouterLink
      to={{
        pathname: newHnaRequestRoute,
        state: {
          partialHnaRequest: {
            missionPlanId: props.missionPlanId?.toString(),
            antennaType: props.antennaType,
            // MPT uses suffixed tail numbers, but the HNA request form expects non-suffixed tail numbers.
            tailNumbers: props.tailNumber ? [ props.tailNumber.replace(/-CWS[1-2]/, "") ] : [],
            hnaZones: props.hnaZones
          } as Partial<HnaRequest>
        }
      }}
      style={{ textDecoration: 'none' }}
    >
      <BeamMenuItem>Prefilled HNA Request</BeamMenuItem>
    </RouterLink>
  );
  if (isMissingHnaZones) {
    prefilledMenuItem = (
      <BeamTooltip title={toolTipMessage(props.hnaZones)} placement={"left"}>
        <BeamMenuItem disabled={true}>Prefilled HNA Request</BeamMenuItem>
      </BeamTooltip>
    );
  }

  return (
    <>
      <Button
        id={'generate-hna-request-button'}
        variant="outlined"
        color="secondary"
        startIcon={<PlaylistAddCheckRounded />}
        aria-controls={isOpen ? 'hna-request-action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
        onClick={handleClick}
      >
        Generate HNA Request
      </Button>

      <BeamMenu
        id="hna-request-action-menu"
        open={isOpen}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'generate-hna-request-button',
          style: { width: anchorEl?.clientWidth }
        }}
        anchorEl={anchorEl}
      >
        {prefilledMenuItem}
        <RouterLink to={newHnaRequestRoute} style={{ textDecoration: 'none' }}>
          <BeamMenuItem>Blank HNA Request</BeamMenuItem>
        </RouterLink>
      </BeamMenu>
    </>
  );
};

const toolTipMessage = (hnaZones: HnaZone[] | undefined): string => {
  let message = 'Could not communicate with the server';

  if (!isNil(hnaZones) && hnaZones.length === 0) {
    message = 'No HNA zones in this flight path';
  }

  return message;
};

export default GenerateHnaRequestButton;
